import { IconDefinition, IconName, config } from "@fortawesome/fontawesome-svg-core";
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faAlignJustify,
  faHighlighter,
  faIndent,
  faLinkSlash,
  faListOl,
  faListUl,
  faOutdent,
  faStrikethrough,
  faCode,
  faEye,
  faAnglesLeft,
  faAnglesRight,
  faAt,
  faDropletSlash,
  faLink,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowRotateLeft,
  faArrowRotateRight,
  faArrowUp,
  faArrowsRotate,
  faBars,
  faCartShopping,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faClone,
  faDesktop,
  faEllipsis,
  faFilter,
  faFont,
  faHandPointer,
  faGripVertical,
  faImage,
  faMagnifyingGlass,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faMinus,
  faMobileScreenButton,
  faNewspaper,
  faPen,
  faPlus,
  faSave,
  faShareNodes,
  faTableColumns,
  faSort,
  faT,
  faTabletScreenButton,
  faTrash,
  faUpRightFromSquare,
  faXmark,
  faCircleInfo,
  faClipboard,
  faClock,
  faDotCircle,
  faEnvelope,
  faFileExport,
  faFileAlt,
  faArrowsUpDownLeftRight,
  faQrcode,
  faQuestion,
  faReceipt,
  faRectangleList,
  faRefresh,
  faTag,
  faGear,
  faSquare,
  faTicket,
  faTimes,
  faUser,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

// As the legacy icon list does, so whichever loads first leaves the same setting.
config.autoAddCss = false;

/** The icons the page builder uses, under the same names as the legacy list. */
export {
  faCheck as ActivateIcon,
  faAlignCenter as AlignCenterIcon,
  faAlignLeft as AlignLeftIcon,
  faAlignRight as AlignRightIcon,
  faAlignJustify as AlignJustifyIcon,
  faHighlighter as HighlightIcon,
  faIndent as IndentIcon,
  faLinkSlash as UnlinkIcon,
  faListOl as NumberedListIcon,
  faListUl as BulletListIcon,
  faOutdent as OutdentIcon,
  faStrikethrough as StrikethroughIcon,
  faAt as AttributeIcon,
  faDropletSlash as ClearColourIcon,
  faLink as LinkIcon,
  faCode as CodeIcon,
  faEye as ViewIcon,
  faAnglesLeft as AnglesLeft,
  faAnglesRight as AnglesRight,
  faArrowDown as ArrowDown,
  faArrowLeft as ArrowLeft,
  faArrowRight as ArrowRight,
  faArrowUp as ArrowUp,
  faBars as Bars,
  faCheck as CheckIcon,
  faChevronDown as ChevronDown,
  faChevronLeft as ChevronLeft,
  faChevronRight as ChevronRight,
  faChevronUp as ChevronUp,
  faCartShopping as CartIcon,
  faClone as CloneIcon,
  faXmark as CrossIcon,
  faTrash as DeleteIcon,
  faDesktop as DesktopIcon,
  faPen as EditIcon,
  faEllipsis as EllipsisIcon,
  faFilter as FilterIcon,
  faFont as TextIcon,
  faT as PlainTextIcon,
  faHandPointer as ButtonIcon,
  faImage as ImageIcon,
  faGripVertical as DragHandleIcon,
  faMagnifyingGlass as MagnifyIcon,
  faMagnifyingGlassMinus as ZoomOutIcon,
  faMagnifyingGlassPlus as ZoomInIcon,
  faMinus as MinusIcon,
  faMobileScreenButton as MobileIcon,
  faNewspaper as TextImageIcon,
  faUpRightFromSquare as OpenInNewTabIcon,
  faPlus as PlusIcon,
  faSort as SortIcon,
  faArrowRotateRight as RedoIcon,
  faArrowsRotate as RotateIcon,
  faSave as SaveIcon,
  faShareNodes as SocialIcon,
  faTableColumns as ColumnsIcon,
  faTabletScreenButton as TabletIcon,
  faArrowRotateLeft as UndoIcon,
  faCircleInfo as CircleInfo,
  faClipboard as ClipboardIcon,
  faClock as ClockIcon,
  faDotCircle as DotCircleIcon,
  faEnvelope as EnvelopeIcon,
  faFileExport as ExportIcon,
  faFileAlt as FileIcon,
  faHandPointer as HandIcon,
  faArrowsUpDownLeftRight as MoveIcon,
  faQrcode as QrcodeIcon,
  faQuestion as QuestionIcon,
  faReceipt as ReceiptIcon,
  faRectangleList as RectangleListIcon,
  faRefresh as RefreshIcon,
  faTag as RenameIcon,
  faGear as SettingsIcon,
  faSquare as SquareIcon,
  faTicket as TicketIcon,
  faTimes as TimesIcon,
  faUser as UserIcon,
  faExclamationCircle as WarningIcon,
};

// Vertical alignment, as the Material icons draw it: an arrow to a line at the top, middle or bottom.
const verticalAlignIcon = (name: string, path: string): IconDefinition => ({
  prefix: "fas",
  iconName: name as IconName,
  icon: [24, 24, [], "", path],
});

// A text area's resize grip: two diagonal lines, the upper one longer.
export const ResizeIcon: IconDefinition = {
  prefix: "fas",
  iconName: "resize-grip" as IconName,
  icon: [
    16,
    16,
    [],
    "",
    "M14.2 1.8l1.4 1.4L3.2 15.6l-1.4-1.4zM14.2 8.2l1.4 1.4-6 6-1.4-1.4z",
  ],
};

export const AlignTopIcon = verticalAlignIcon("align-top", "M8 11h3v10h2V11h3l-4-4-4 4zM4 3v2h16V3H4z");
export const AlignMiddleIcon = verticalAlignIcon(
  "align-middle",
  "M8 19h3v4h2v-4h3l-4-4-4 4zm8-14h-3V1h-2v4H8l4 4 4-4zM4 11v2h16v-2H4z"
);
export const AlignBottomIcon = verticalAlignIcon("align-bottom", "M16 13h-3V3h-2v10H8l4 4 4-4zM4 19v2h16v-2H4z");
